import {
    JsonRpcRequest,
    JsonRpcResponse,
    JsonRpcNotification
} from "./types";


let ws: WebSocket | null = null;
let requestId = 0;
const pending = new Map<number, (value: JsonRpcResponse) => void>();
const pendingQueue: Array<{ method: string; params: any; resolve: (value: JsonRpcResponse) => void }> = [];

// Notification subscriptions
const notificationHandlers = new Map<string, Set<(params: any) => void>>();

// Connection state
let isConnecting = false;
let connectionPromise: Promise<void> | null = null;

// Determine WebSocket URL based on environment
function getWebSocketUrl(): string {
    // Check if there's an environment variable
    const envUrl = import.meta.env.VITE_WS_URL;
    if (envUrl) {
        return envUrl;
    }
    
    // In production, use the current host
    if (import.meta.env.PROD) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/ws`;
    }

    // In development, use the current Vite origin and let the dev proxy forward /ws.
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
}


export function connectWS(
    onNotification?: (data: JsonRpcNotification) => void
): WebSocket {
    const wsUrl = getWebSocketUrl();
    console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);
    
    // Check for protocol mismatch
    if (window.location.protocol === 'https:' && wsUrl.startsWith('ws://')) {
        console.warn('⚠️ SECURITY WARNING: Trying to connect to unsecured WebSocket from HTTPS page!');
        console.warn('   This may be blocked by browser. Consider using WSS (secure WebSocket) or nginx reverse proxy.');
    }
    
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
        isConnecting = false;
        connectionPromise = null;
        console.log('✅ WebSocket connected successfully');
        
        // Process pending queue
        while (pendingQueue.length > 0) {
            const { method, params, resolve } = pendingQueue.shift()!;
            console.log(`📤 Processing queued request: ${method}`);
            rpcCall(method, params).then(resolve);
        }
    };

    ws.onmessage = (event: MessageEvent<string>) => {
        const data = JSON.parse(event.data);

        if ("id" in data && pending.has(data.id)) {
            pending.get(data.id)!(data);
            pending.delete(data.id);
        } else {
            // Call main notification handler
            onNotification?.(data);
            
            // Call method-specific handlers
            if (data.method && notificationHandlers.has(data.method)) {
                const handlers = notificationHandlers.get(data.method)!;
                handlers.forEach(handler => {
                    try {
                        handler(data.params);
                    } catch (error) {
                        console.error(`Error in notification handler for ${data.method}:`, error);
                    }
                });
            }
        }
    };

    ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        console.error(`   URL: ${wsUrl}`);
        console.error(`   Page protocol: ${window.location.protocol}`);
        if (window.location.protocol === 'https:' && wsUrl.startsWith('ws://')) {
            console.error('   ⚠️ PROTOCOL MISMATCH: Use WSS for HTTPS pages or set up reverse proxy (nginx)');
        }
    };

    ws.onclose = (event) => {
        console.warn('⚠️ WebSocket closed');
        console.warn(`   Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        console.warn(`   Clean: ${event.wasClean}`);
        isConnecting = false;
        connectionPromise = null;
    };

    return ws;
}


/**
 * Wait for WebSocket connection with timeout
 */
async function waitForConnection(maxWaitMs: number = 5000): Promise<boolean> {
    if (ws && ws.readyState === WebSocket.OPEN) {
        return true;
    }

    // If already connecting, wait for that promise
    if (connectionPromise) {
        try {
            await connectionPromise;
            return ws !== null && ws.readyState === WebSocket.OPEN;
        } catch {
            return false;
        }
    }

    // Wait for connection with timeout
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
        if (ws && ws.readyState === WebSocket.OPEN) {
            return true;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    return false;
}

export function rpcCall<P = unknown, R = unknown>(
    method: string,
    params?: P,
    options?: { timeout?: number; queueIfDisconnected?: boolean }
): Promise<JsonRpcResponse<R>> {
    const timeout = options?.timeout || 30000; // 30s default
    const queueIfDisconnected = options?.queueIfDisconnected !== false; // true by default

    return new Promise(async (resolve) => {
        // Check if connected
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.warn(`⚠️ WebSocket not ready for ${method}, state: ${ws?.readyState}`);
            
            // Try to wait for connection
            const connected = await waitForConnection(5000);
            
            if (!connected) {
                if (queueIfDisconnected) {
                    console.log(`📥 Queueing request: ${method}`);
                    pendingQueue.push({ method, params, resolve: resolve as any });
                    return;
                }
                
                resolve({
                    jsonrpc: "2.0",
                    id: 0,
                    error: {
                        code: -1,
                        message: "WebSocket is not connected. Please wait for reconnection."
                    }
                } as JsonRpcResponse<R>);
                return;
            }
        }

        const id = ++requestId;

        const payload: JsonRpcRequest<P> = {
            jsonrpc: "2.0",
            method,
            params,
            id
        };

        // Set timeout for request
        const timeoutId = setTimeout(() => {
            if (pending.has(id)) {
                pending.delete(id);
                resolve({
                    jsonrpc: "2.0",
                    id,
                    error: {
                        code: -1,
                        message: `Request timeout after ${timeout}ms`
                    }
                } as JsonRpcResponse<R>);
            }
        }, timeout);

        pending.set(id, (response) => {
            clearTimeout(timeoutId);
            resolve(response as JsonRpcResponse<R>);
        });
        
        try {
            ws!.send(JSON.stringify(payload));
            console.log(`📤 Sent: ${method}`);
        } catch (error) {
            clearTimeout(timeoutId);
            pending.delete(id);
            resolve({
                jsonrpc: "2.0",
                id,
                error: {
                    code: -1,
                    message: error instanceof Error ? error.message : "Failed to send request"
                }
            } as JsonRpcResponse<R>);
        }
    });
}

/**
 * Get current WebSocket connection state
 */
export function getConnectionState(): {
    connected: boolean;
    readyState: number | null;
    queueLength: number;
} {
    return {
        connected: ws !== null && ws.readyState === WebSocket.OPEN,
        readyState: ws?.readyState ?? null,
        queueLength: pendingQueue.length
    };
}

/**
 * Get WebSocket instance (for debugging)
 */
export function getWebSocket(): WebSocket | null {
    return ws;
}

/**
 * Subscribe to WebSocket notifications
 * @param method - Notification method name (e.g. "games.updated")
 * @param handler - Callback function to handle notification params
 * @returns Unsubscribe function
 */
export function subscribe(method: string, handler: (params: any) => void): () => void {
    if (!notificationHandlers.has(method)) {
        notificationHandlers.set(method, new Set());
    }
    
    notificationHandlers.get(method)!.add(handler);
    console.log(`📥 Subscribed to ${method}`);
    
    // Return unsubscribe function
    return () => {
        const handlers = notificationHandlers.get(method);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                notificationHandlers.delete(method);
            }
            console.log(`📤 Unsubscribed from ${method}`);
        }
    };
}